import axios from 'axios';
import { createRouter, createWebHistory } from 'vue-router';

import { useBranding } from '@/composables/useBranding';
import { useUserLocale } from '@/composables/useUserLocale';
import { getConfig } from '@/config/fetchEnv';
import { i18next, t } from '@/config/i18n';
import { useAuthStore } from '@/stores/auth';
import { useOnboardingStore } from '@/stores/onboarding';
import { useUserStore } from '@/stores/user';

function isCaptchaSolved(): boolean {
  return document.cookie.split(';').some(c => c.trim().startsWith('af-captcha='));
}

/** True if the server-set `af-auth` indicator cookie is present. */
function isLoggedIn(): boolean {
  return document.cookie.split(';').some(c => c.trim().startsWith('af-auth='));
}

/** Paths (prefix-matched) and route names that never require authentication. */
const PUBLIC_PATH_PREFIXES = ['/public', '/register', '/forgot-password', '/reset-password'];
const PUBLIC_ROUTE_NAMES = new Set([
  'login',
  'login-captcha',
  'logout',
  'not-found',
  'help',
  'self-register-candidate',
  'self-register-recruiter',
  'short-link'
]);

function isPublicRoute(path: string, name: string | symbol | null | undefined): boolean {
  if (PUBLIC_ROUTE_NAMES.has(name as string)) return true;
  return PUBLIC_PATH_PREFIXES.some(p => path.startsWith(p));
}

/**
 * Routes that must NOT be diverted to an SSO login page even when the visitor
 * carries an `?authRedirect=<slug>` param: account activation and the
 * forgot/reset-password flow. These are opened from an email link and complete
 * on their own before any session exists — bouncing them to SSO would break them.
 */
const AUTH_REDIRECT_EXCLUDED_NAMES = new Set([
  'register-activation',
  'forgot-password',
  'forgot-password-success',
  'forgot-password-expired',
  'reset-password',
  'reset-password-success'
]);

/** Invite-triggering query params (non-public routes only). */
const INVITE_TRIGGER_KEYS = ['invited_by', 'invited_by_recruiter', 'screening'];

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Home (redirect to login if not authenticated or redirect depends on the user flow)
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/HomeView.vue')
    },

    // Logout
    {
      path: '/logout',
      name: 'logout',
      component: () => import('@/views/LogoutView.vue')
    },

    // Captcha (must be before /login)
    {
      path: '/login/captcha',
      name: 'login-captcha',
      component: () => import('@/views/CaptchaView.vue'),
      meta: { title: 'login.captcha.meta.title' }
    },

    // Login
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { title: 'login.meta.title' }
    },

    // Forgot password flow
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { title: 'reset_password.meta.title' }
    },
    {
      path: '/forgot-password/success',
      name: 'forgot-password-success',
      component: () => import('@/views/ForgotPasswordSuccessView.vue'),
      meta: { title: 'reset_password.meta.title' }
    },
    {
      path: '/forgot-password/expired',
      name: 'forgot-password-expired',
      component: () => import('@/views/ForgotPasswordExpiredView.vue'),
      meta: { title: 'reset_password.meta.title' }
    },

    // Reset password flow (success before :token to avoid conflict)
    {
      path: '/reset-password/success',
      name: 'reset-password-success',
      component: () => import('@/views/ResetPasswordSuccessView.vue'),
      meta: { title: 'reset_password.meta.title' }
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { title: 'reset_password.meta.title' }
    },

    // Register — parcours unique (plus de :scope depuis la suppression du business)
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/register/RegisterLandingView.vue'),
      meta: { title: 'register.meta.title' }
    },
    {
      path: '/register/sign-up',
      name: 'register-signup',
      component: () => import('@/views/register/RegisterSignUpView.vue'),
      meta: { title: 'register.meta.title' }
    },
    {
      path: '/register/introduce',
      name: 'register-introduce',
      component: () => import('@/views/register/RegisterIntroduceView.vue'),
      meta: { title: 'register.value.personal_self.meta' }
    },
    {
      path: '/register/confirmation',
      name: 'register-confirmation',
      component: () => import('@/views/register/RegisterConfirmationView.vue')
    },
    {
      path: '/register/activation',
      name: 'register-activation',
      component: () => import('@/views/register/RegisterActivationView.vue'),
      meta: { title: 'login.meta.mfa' }
    },

    {
      path: '/register/success',
      name: 'register-success',
      component: () => import('@/views/register/RegisterSuccessView.vue')
    },

    // Register screening
    {
      path: '/register/screening',
      name: 'register-screening',
      component: () => import('@/views/register/RegisterScreeningView.vue'),
      meta: { title: 'register.landing.personal.step_1.meta' }
    },

    // Register — anciennes URLs scopées (/register/personal/…, /register/business/…).
    // Les liens déjà partis (emails Viktor, workflows) et OAuthController, qui redirige
    // encore vers /register/{scope}/introduce, doivent continuer à tomber sur le parcours.
    // Le scope est contraint pour ne pas masquer /register/sign-up & co.
    {
      path: '/register/:scope(personal|business)',
      redirect: to => ({ name: 'register', params: {}, query: to.query, hash: to.hash })
    },
    {
      path: '/register/:scope(personal|business)/sign-up',
      redirect: to => ({ name: 'register-signup', params: {}, query: to.query, hash: to.hash })
    },
    {
      path: '/register/:scope(personal|business)/introduce',
      redirect: to => ({ name: 'register-introduce', params: {}, query: to.query, hash: to.hash })
    },
    {
      path: '/register/:scope(personal|business)/confirmation',
      redirect: to => ({ name: 'register-confirmation', params: {}, query: to.query, hash: to.hash })
    },

    // Onboarding (personal account post-register)
    {
      path: '/onboarding/loading',
      name: 'onboarding-loading',
      component: () => import('@/views/onboarding/OnboardingLoadingView.vue')
    },
    {
      path: '/onboarding/work-experience',
      name: 'onboarding-work-experience',
      component: () => import('@/views/onboarding/OnboardingWorkExperienceView.vue'),
      meta: { title: 'onboarding.level_1.step_2.meta' }
    },
    {
      path: '/onboarding/pronouns',
      name: 'onboarding-pronouns',
      component: () => import('@/views/onboarding/OnboardingPronounsView.vue'),
      meta: { title: 'onboarding.level_3.step_3.meta' }
    },

    // MFA
    {
      path: '/mfa',
      name: 'mfa',
      component: () => import('@/views/MfaView.vue'),
      meta: { title: 'mfa.set_up.title' }
    },
    {
      path: '/mfa/code',
      name: 'mfa-code',
      component: () => import('@/views/MfaCodeView.vue'),
      meta: { title: 'login.meta.mfa' }
    },

    // Spaces
    {
      path: '/spaces',
      name: 'spaces',
      component: () => import('@/views/SpacesView.vue'),
      meta: { title: 'login.space.meta' }
    },

    // Customer bloqué — recruteur / manager dont le customer n'a plus d'accès pro
    {
      path: '/customer-blocked',
      name: 'customer-blocked',
      component: () => import('@/views/CustomerBlockedView.vue')
    },

    // Terms of use
    {
      path: '/terms-of-use',
      name: 'terms-of-use',
      component: () => import('@/views/TermsOfUseView.vue')
    },

    // Help / support
    {
      path: '/help',
      name: 'help',
      component: () => import('@/views/HelpView.vue'),
      meta: { title: 'login.meta.title' }
    },

    // Invitations
    {
      path: '/invitations/role',
      name: 'invitations-role',
      component: () => import('@/views/invitations/InvitationsRoleView.vue'),
      meta: { title: 'manage_network.new_invite.meta.role' }
    },
    {
      path: '/invitations/share-profile',
      name: 'invitations-share-profile',
      component: () => import('@/views/invitations/InvitationsShareProfileView.vue'),
      meta: { title: 'manage_network.new_invite.meta.network' }
    },

    // Public pages
    {
      path: '/public/status/error',
      name: 'public-status-error',
      component: () => import('@/views/public/StatusErrorView.vue')
    },
    {
      path: '/public/status/409',
      name: 'public-status-409',
      component: () => import('@/views/public/Status409View.vue')
    },
    {
      path: '/public/status/thank-you',
      name: 'public-status-thank-you',
      component: () => import('@/views/public/StatusThankYouView.vue')
    },
    {
      path: '/public/expired/campaign',
      name: 'public-expired-campaign',
      component: () => import('@/views/public/ExpiredCampaignView.vue')
    },
    {
      path: '/public/expired/link',
      name: 'public-expired-link',
      component: () => import('@/views/public/ExpiredLinkView.vue')
    },
    {
      path: '/public/workflow/expired',
      name: 'public-workflow-expired',
      component: () => import('@/views/public/WorkflowExpiredView.vue'),
      meta: { title: 'candidate_account.manage_process.meta.title' }
    },
    { path: '/public/invitation', redirect: '/register/personal' },
    {
      path: '/public/invitation/refuse',
      name: 'public-invitation-refuse',
      component: () => import('@/views/public/InvitationRefuseView.vue')
    },
    // LEGACY — remplacé par /public/invitation/workflow. Plus aucun mail n'envoie
    // ici ; à supprimer quand les tokens des anciens mails Screening Start / Poke
    // auront expiré côté Viktor (penser à purger les caches S3 du front).
    {
      path: '/public/invitation/screening',
      name: 'public-invitation-screening',
      component: () => () => {},
      beforeEnter: async to => {
        try {
          const token = to.query.token ? String(to.query.token) : '';
          const { data } = await axios.get('/api/screening/invite', {
            params: token ? { token } : {}
          });
          const dest = String(data?.redirect);
          // Absolute URL (e.g. assessments app) → full browser navigation.
          // Relative path (SPA route) → let the Vue router handle it.
          if (dest.startsWith('http')) {
            window.location.href = dest;
            return false;
          }
          return dest;
        } catch {
          return '/public/expired/link';
        }
      }
    },
    {
      path: '/public/invitation/workflow',
      name: 'public-invitation-workflow',
      component: () => () => {},
      beforeEnter: async to => {
        try {
          // Forward the whole query string: with a token the gateway reads the
          // invitation from Viktor, without one it falls back to the shortlink
          // params (invited_by_recruiter, sharepool, campaign…).
          const { data } = await axios.get('/api/workflow/invite', { params: to.query });
          const dest = String(data?.redirect);
          // Absolute URL (e.g. assessments app) → full browser navigation.
          // Relative path (SPA route) → let the Vue router handle it.
          if (dest.startsWith('http')) {
            window.location.href = dest;
            return false;
          }
          return dest;
        } catch {
          return '/public/expired/link';
        }
      }
    },

    {
      path: '/public/network/invite',
      name: 'public-network-invite',
      component: () => import('@/views/public/NetworkInviteView.vue')
    },
    {
      path: '/public/network/invite/refuse',
      name: 'public-network-invite-refuse',
      component: () => import('@/views/public/NetworkInviteRefuseView.vue')
    },
    {
      path: '/public/network/invite/refuse/success',
      name: 'public-network-invite-refuse-success',
      component: () => import('@/views/public/NetworkInviteRefuseSuccessView.vue')
    },
    {
      path: '/public/network/invite/upload-cv',
      name: 'public-network-invite-upload-cv',
      component: () => import('@/views/public/NetworkInviteUploadCvView.vue')
    },
    {
      path: '/public/sourcing/invitation',
      name: 'public-sourcing-invitation',
      component: () => import('@/views/public/SourcingInvitationView.vue')
    },
    {
      path: '/public/sourcing/status',
      name: 'public-sourcing-status',
      component: () => import('@/views/public/SourcingStatusView.vue')
    },
    {
      path: '/public/update-email',
      name: 'public-update-email',
      component: () => import('@/views/public/UpdateEmailView.vue')
    },
    {
      path: '/public/oauth-redirect',
      name: 'public-oauth-redirect',
      component: () => import('@/views/public/OauthRedirectView.vue')
    },

    // Legacy entry-point paths — Vue catches the bare URL, calls the Laravel API
    // (which uses the configured baseURL in prod), and follows the returned redirect.
    {
      path: '/self-register-candidate/:publicKey(.*)',
      name: 'self-register-candidate',
      component: () => {},
      async beforeEnter(to) {
        try {
          const { data } = await axios.get(`/api/self-register-candidate/${to.params.publicKey}`, {
            params: to.query
          });
          window.location.href = data.redirect;
        } catch {
          window.location.href = '/public/expired/link';
        }
        return false;
      }
    },
    {
      path: '/self-register/:publicKey/:campaignId?',
      name: 'self-register-recruiter',
      component: () => {},
      async beforeEnter(to) {
        try {
          const campaign = to.params.campaignId ? `/${to.params.campaignId}` : '';
          const { data } = await axios.get(`/api/self-register/${to.params.publicKey}${campaign}`);
          window.location.href = data.redirect;
        } catch {
          window.location.href = '/public/expired/link';
        }
        return false;
      }
    },
    {
      path: '/short-link/:publicKey(.*)',
      name: 'short-link',
      component: () => {},
      async beforeEnter(to) {
        try {
          const { data } = await axios.get(`/api/short-link/${to.params.publicKey}`, {
            params: to.query
          });
          window.location.href = data.redirect;
        } catch {
          window.location.href = '/public/expired/link';
        }
        return false;
      }
    },

    // 404 — redirection, comme af/auth (app/[locale]/not-found.tsx fait un
    // redirect vers /login). Rendre LoginView ici court-circuitait le garde
    // captcha du dessous, qui ne regarde que la route nommée `login` : n'importe
    // quelle URL inconnue (/login/pepito) donnait le formulaire sans challenge.
    // La query est conservée pour ne pas perdre les paramètres d'invitation.
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: to => ({ name: 'login', query: to.query })
    }
  ]
});

router.beforeEach(async to => {
  // ── db (ATS reseller) — persist ?db=<partner> as a cookie ────────────────
  // The landing URL carries ?db=partner but the user has no Viktor token yet;
  // the gateway's global HTTP middleware reads this cookie and forwards it to
  // Viktor as the X-AF-DB header to switch DB. Mirrors af/auth proxy.ts — but
  // since the SPA is served from S3 (Laravel is a pure /api gateway) we persist
  // it client-side instead of in a server middleware. SameSite=None so it
  // survives an ATS partner iframe embedding.
  if (to.query.db) {
    document.cookie = `af-db=${encodeURIComponent(String(to.query.db))}; path=/; max-age=3600; SameSite=None; Secure`;
  }

  // ── 0. forceRedirect query param — delegate to Laravel, which validates
  //        the URL and sets the httpOnly af-force-redirect cookie. ─────────
  if (to.query.forceRedirect) {
    const cleanQuery = { ...to.query };
    delete cleanQuery.forceRedirect;
    const cleanSearch = new URLSearchParams(cleanQuery as Record<string, string>).toString();
    const next = to.path + (cleanSearch ? '?' + cleanSearch : '');
    try {
      const { data } = await axios.get('/api/force-redirect', {
        params: { url: String(to.query.forceRedirect), next }
      });
      window.location.href = data.next;
    } catch {
      window.location.href = next;
    }
    return false;
  }

  // ── 0a. ?sso=<slug> on /login — redirect to SSO provider login page ─────
  // Mirrors af/auth login/page.tsx: if (_searchParams.sso) redirect(`${AF_REDIRECT_SSO}/${slug}/login`)
  if (to.name === 'login' && to.query.sso) {
    try {
      const config = getConfig();
      const slug = String(to.query.sso).trim();
      if (slug && config.redirect_sso) {
        window.location.href = `${config.redirect_sso}/${slug}/login`;
        return false;
      }
    } catch {
      // config not yet loaded — fall through to normal login
    }
  }

  // ── 0b. SSO organisation — deep-link a logged-out visitor to its SSO login ──
  // Mirrors phoenix (Viktor.ts 401 interceptor) and assessments: an
  // `?authRedirect=<slug>` param means the visitor belongs to an SSO org and
  // must authenticate on that org's own login page; `af` (or no param) means the
  // standard AssessFirst login. `forceRedirect` brings them back to the exact
  // URL they were trying to reach once authenticated.
  // Not logged in = no `af-auth` indicator cookie (set by the gateway once an
  // access_token exists). Excluded: account activation + forgot/reset password.
  //
  // MUST stay ahead of block 0d: that one answers `{ path: to.path }` on an
  // invite link, which drops the whole query string — `authRedirect` included —
  // so a later check would never see the param. Running first also keeps the
  // full invite query in the return URL, so the SSO sends the visitor back to
  // the complete link and 0d prefills the registration then.
  if (!isLoggedIn() && !AUTH_REDIRECT_EXCLUDED_NAMES.has(to.name as string)) {
    const slug = to.query.authRedirect ? String(to.query.authRedirect).trim() : '';
    if (slug && slug !== 'af') {
      try {
        const config = getConfig();
        if (config.redirect_sso) {
          // Build the return URL WITHOUT `authRedirect` so that landing back here
          // after the SSO login can't re-trigger this redirect (infinite-loop
          // guard). Same pattern as the `forceRedirect` cleanup in block 0.
          const returnQuery = { ...to.query };
          delete returnQuery.authRedirect;
          const returnSearch = new URLSearchParams(returnQuery as Record<string, string>).toString();
          const returnUrl = window.location.origin + to.path + (returnSearch ? `?${returnSearch}` : '');
          const forceRedirect = encodeURIComponent(returnUrl);
          window.location.href = `${config.redirect_sso}/${slug}/login?forceRedirect=${forceRedirect}`;
          return false;
        }
      } catch {
        // config not yet loaded — fall through to the normal login flow
      }
    }
  }

  // ── 0c. ?from=screening — pre-fill email and go to register ────────────
  // The screening app redirects back here with ?from=screening&email=xxx.
  // Store the email in the register Pinia store and strip the params.
  if (to.query.from === 'screening' && to.query.email) {
    const { useRegisterStore } = await import('@/stores/register');
    const registerStore = useRegisterStore();

    await axios.post('/api/auth/logout').catch(() => {});
    useUserStore().clear();

    registerStore.setEmail(String(to.query.email));
    // Redirect to the same path without the screening params
    const cleanQuery = Object.fromEntries(Object.entries(to.query).filter(([k]) => k !== 'from' && k !== 'email'));
    return { path: to.path, query: cleanQuery };
  }

  // ── 0d. /register with invite params ────────────────────────────────
  // Mirrors af/auth middleware: when landing on /register?token=...
  // &invited_by_recruiter=...&invited_email=...&role=... etc., extract params,
  // prefill the register store, then redirect to the clean URL so the landing
  // page renders without query string (same pattern as Next.js middleware).
  // Les anciennes URLs /register/{personal,business} y arrivent via les
  // redirections déclarées plus haut, qui conservent la query.
  const inviteParamKeys = ['token', 'role', 'invited_by_recruiter', 'invited_by'];
  const isRegisterLanding = to.path === '/register' || to.path === '/register/';
  const hasInviteQueryParams = inviteParamKeys.some(k => to.query[k] !== undefined);

  if (isRegisterLanding && hasInviteQueryParams) {
    // Already authenticated — typically back from the SSO login sent by block 0b,
    // which returns here with the invite query intact. There is nothing to
    // register: hand the params to the server, which attaches the invitation to
    // the session's user and answers with the next step of the user flow. Same
    // call as block 4 for a logged-in visitor, and as navigateAfterLogin() in
    // LoginView — /register/* is excluded there, so without this the SSO user
    // would be dropped back into the multi-step registration below.
    if (isLoggedIn()) {
      const params = Object.fromEntries(Object.entries(to.query).map(([k, v]) => [k, String(v ?? '')]));
      try {
        const { data } = await axios.post('/api/invite', params);
        const dest = data.redirect as string;
        if (dest.startsWith('http')) {
          window.location.href = dest;
          return false;
        }
        return dest;
      } catch {
        // Server refused (stale session, invite already consumed…) — fall back to
        // the login page rather than a half-initialised registration tunnel.
        return { name: 'login' };
      }
    }

    // Screening shortlink — divert to the dedicated screening email form.
    // Mirrors af/auth middleware which redirects /register/personal?screening=1
    // to the screening flow. Params must go in the AUTH store (RegisterScreeningView
    // reads auth.pendingInviteParams) and we must NOT fall through to the normal
    // multi-step registration below — otherwise the screening context is lost.
    const isScreening = to.query.screening === 'true' || to.query.screening === '1';
    if (isScreening && to.query.invited_by_recruiter) {
      const params = Object.fromEntries(Object.entries(to.query).map(([k, v]) => [k, String(v ?? '')]));
      useAuthStore().setPendingInviteParams(params);
      return '/register/screening';
    }

    const { useRegisterStore } = await import('@/stores/register');
    const registerStore = useRegisterStore();

    // Decode invited_email — %2B is decoded to + by the URL parser,
    // but a raw + in the query string would become a space → restore it.
    const rawEmail = to.query.invited_email as string | undefined;
    if (rawEmail) {
      registerStore.setEmail(rawEmail.replace(/ /g, '+'));
    }

    // Prefill the candidate name (workflow token → /register/personal?first_name=…).
    // Must go through the store: the query string is stripped just below, so the
    // introduce step can no longer read it from the URL.
    const queryFirstName = to.query.first_name as string | undefined;
    const queryLastName = to.query.last_name as string | undefined;
    if (queryFirstName || queryLastName) {
      registerStore.setIntroduce({
        firstName: queryFirstName ?? registerStore.firstName,
        lastName: queryLastName ?? registerStore.lastName,
        jobTitle: registerStore.jobTitle
      });
    }

    // Persist all invite params (token, invited_by_recruiter, sharepool,
    // sharepool_token, campaign, utm_*, company_label, etc.) for use in
    // the registration API call (sent as `params` in the request body).
    const params = Object.fromEntries(Object.entries(to.query).map(([k, v]) => [k, String(v ?? '')]));
    registerStore.setInviteParams(params);

    // Also keep them in the auth store: an EXISTING user landing here switches to the
    // login form, and navigateAfterLogin() reads them from there to POST /api/invite.
    // Without this the sharepool/network add never happens for an existing candidate —
    // no invitation is created, so the user-flow sends them straight to the assessments.
    // af/auth kept one shared auth session for both paths (proxy.ts → login _actions.ts).
    useAuthStore().setPendingInviteParams(params);

    // Fetch recruiter branding if available — mirrors af/auth middleware
    // which calls getBrandingFromRecruiter(invited_by_recruiter, sharepool).
    // Silently ignored on failure (branding is non-blocking).
    const recruiterUuid = params.invited_by_recruiter;
    if (recruiterUuid) {
      try {
        const sharepoolUuid = params.sharepool;
        const brandingUrl = sharepoolUuid
          ? `/api/branding/recruiter/${recruiterUuid}?sharepool_uuid=${sharepoolUuid}`
          : `/api/branding/recruiter/${recruiterUuid}`;
        const { data } = await axios.get(brandingUrl);
        if (data?.branding) {
          registerStore.setBranding(data.branding);
        }
      } catch {
        // Non-blocking — default AssessFirst branding stays active
      }
    } else if (params.token) {
      // Token-based invitation (e.g. role invite) — branding comes from the token.
      // Mirrors af/auth proxy.ts: getRegisterDataFromToken(token) → token.branding.
      try {
        const { data } = await axios.get(`/api/register/token/${params.token}`);
        const branding = data?.token?.branding;
        if (branding) {
          registerStore.setBranding(branding);
        }
      } catch {
        // Non-blocking — default AssessFirst branding stays active
      }
    }

    // Redirect to clean URL — landing page will render normally
    return { path: '/register' };
  }

  // ── 1b. Invite email while logged in → force logout first ───────────────
  // A logged-in user following an invite link (`/login?invite`) must be logged
  // out so they accept the invitation as the invited identity, not with their
  // current session. Mirrors af/auth proxy.ts (logoutUser on
  // `searchParams.has("invite")`). Must run before block 2, which would
  // otherwise send the logged-in user straight to home.
  if (isLoggedIn() && to.name === 'login' && to.query.invite !== undefined) {
    return { name: 'logout' };
  }

  // ── 2. Authenticated user on login → redirect to home (before captcha check)
  // Must be before the captcha gate — a logged-in user visiting /login should
  // bypass the captcha entirely and be sent straight to their workspace.
  if (isLoggedIn() && (to.name === 'login' || to.name === 'register')) {
    return { name: 'home' };
  }

  // ── 3. Captcha gate on /login ────────────────────────────────────────────
  if (to.name === 'login') {
    if (!isCaptchaSolved()) {
      try {
        const config = getConfig();
        if (config.environment === 'production' || to.query.has_captcha !== 'false') {
          return { name: 'login-captcha' };
        }
      } catch {
        return { name: 'login-captcha' };
      }
    }
  }

  // ── 4. Invite params — process or store for after login ─────────────────
  // Exclude /public/* (token-based pages) and /register/* (registration with
  // invite context — components read params directly from the URL).
  const hasInviteParams =
    !to.path.startsWith('/public') &&
    !to.path.startsWith('/register') &&
    INVITE_TRIGGER_KEYS.some(k => to.query[k] !== undefined);

  if (hasInviteParams) {
    const params = Object.fromEntries(Object.entries(to.query).map(([k, v]) => [k, String(v ?? '')]));

    if (isLoggedIn()) {
      // Authenticated — process invite immediately via server
      try {
        const { data } = await axios.post('/api/invite', params);
        const dest = data.redirect as string;
        if (dest.startsWith('http')) {
          window.location.href = dest;
          return false;
        }
        return dest;
      } catch {
        return { name: 'login' };
      }
    } else {
      // Not authenticated — store params
      const authStore = useAuthStore();
      authStore.setPendingInviteParams(params);

      // Screening flow → needs the email form, not the login form
      const isScreening = params.screening === 'true' || params.screening === '1';
      if (isScreening && params.invited_by_recruiter) {
        return '/register/screening';
      }

      return { name: 'login' };
    }
  }

  // ── 4. Onboarding guard ──────────────────────────────────────────────────
  // If the user is mid-onboarding, keep them on the onboarding flow.
  if (to.name !== 'logout') {
    const onboardingStore = useOnboardingStore();
    if (onboardingStore.uuid && !to.path.startsWith('/onboarding')) {
      return '/onboarding/loading';
    }
  }

  // ── 5. Auth protection ───────────────────────────────────────────────────
  if (!isPublicRoute(to.path, to.name) && !isLoggedIn()) {
    return { name: 'login' };
  }

  // ── 6. Langue du compte ──────────────────────────────────────────────────
  // Le boot part du cookie `locale`, que phoenix n'écrit pas : c'est `me` qui
  // porte la langue du compte. On se réaligne dessus ici, et pas dans les vues,
  // pour que ce soit fait avant le premier rendu (donc sans bascule visible) et
  // pour couvrir aussi /terms-of-use, qui ne charge aucun /me.
  if (!isPublicRoute(to.path, to.name)) {
    await useUserLocale().syncLocaleFromUser();
  }
});

// Per-page document title — mirrors af/auth's generateMetadata + the
// `%s | AssessFirst` template. Routes declare an i18n key in `meta.title`;
// routes without one fall back to the plain app name.
const APP_NAME = 'AssessFirst';
function applyTitle() {
  const key = router.currentRoute.value.meta.title as string | undefined;
  document.title = key ? `${t(key)} | ${APP_NAME}` : APP_NAME;
}
router.afterEach(applyTitle);

// Branding is re-applied after every navigation, the way af/auth's root layout
// recomputes it server-side on every render. App.vue applies it once on mount from
// the persisted store; that is too early for the guards that *resolve* the branding
// during the navigation (workflow / invite / screening set the af-branding cookie in
// their API response), so without this the candidate lands on /login unbranded.
router.afterEach(() => useBranding().applyStoredBranding());

// The router's initial navigation can resolve before i18next has finished loading
// its namespaces (both are kicked off in parallel in app.ts). When that happens the
// afterEach above runs while t() still returns the raw key. Re-apply the current
// route's title whenever translations (re)load or the language changes, so a title
// resolved too early gets corrected — afterEach is not a reactive scope, so t()'s
// own reactivity doesn't cover this.
i18next.on('loaded', applyTitle);
i18next.on('languageChanged', applyTitle);
